/**
 * Top-level-node segmenting for the Post Stream Review toolbar (the `block` /
 * gate-OFF strategy).
 *
 * Derives segments directly from what the AI committed, rather than the
 * prosemirror-changeset `block` diff (whose grouping mis-attributes the original
 * side of structural edits). Each overlap-merged coarse segment carries the
 * `originalUserContentSlice` / `newSlice` for one edited region; we compare their
 * top-level nodes and emit one segment per added / removed / updated node.
 *
 * A coarse segment's `newSlice` occupies exactly `[startPos, endPos]`, so each new
 * node's live range is found by walking node sizes from the first node's true live
 * start (`startPos - newSlice.openStart`; see the walk below). Removals are
 * zero-width, anchored where the node was removed. Granularity is top-level only.
 */
import { Fragment, type Node as PMNode, Slice } from '@atlaskit/editor-prosemirror/model';

import type { AIContentPositionEntry } from '../../../../pm-plugins/ai-session-context/types';

/** Wrap a single complete top-level node in a zero-open slice. */
const wholeNodeSlice = (node: PMNode): Slice => new Slice(Fragment.from(node), 0, 0);

/** The top-level nodes carried by a slice, in order. */
const topLevelNodes = (slice: Slice): PMNode[] => {
	const nodes: PMNode[] = [];
	slice.content.forEach((node) => nodes.push(node));
	return nodes;
};

/**
 * Derive the reviewable top-level-node segments for the PSR toolbar from the
 * overlap-merged coarse segments. Returns segments in new-document reading order
 * (ascending `startPos`). Unchanged top-level nodes produce no segment.
 *
 * For each coarse segment we align its original and new top-level nodes by
 * trimming the common prefix / suffix (so unchanged surrounding nodes are
 * skipped) and pairing the changed middle positionally:
 *   - paired nodes that differ → `update`,
 *   - extra new nodes          → `add`,
 *   - extra original nodes     → `remove`.
 */
export const calculateTopLevelNodeSegments = (
	coarseSegments: AIContentPositionEntry[],
): AIContentPositionEntry[] => {
	const segments: AIContentPositionEntry[] = [];

	for (const coarse of coarseSegments) {
		const originalNodes = topLevelNodes(coarse.originalUserContentSlice);
		const newNodes = topLevelNodes(coarse.newSlice);

		// Trim the common prefix, then the common suffix, so only the genuinely
		// changed nodes remain in the middle. Node equality is exact (`.eq`), so an
		// attribute-only change (e.g. panel type) still counts as a change.
		let prefix = 0;
		while (
			prefix < originalNodes.length &&
			prefix < newNodes.length &&
			originalNodes[prefix].eq(newNodes[prefix])
		) {
			prefix++;
		}
		let suffix = 0;
		while (
			suffix < originalNodes.length - prefix &&
			suffix < newNodes.length - prefix &&
			originalNodes[originalNodes.length - 1 - suffix].eq(newNodes[newNodes.length - 1 - suffix])
		) {
			suffix++;
		}

		const originalMiddle = originalNodes.slice(prefix, originalNodes.length - suffix);
		const newMiddle = newNodes.slice(prefix, newNodes.length - suffix);

		// Walk the live-doc cursor through every new node (prefix included) so each
		// changed node lands at its true `[startPos, endPos]`.
		//
		// The cursor base is `startPos - newSlice.openStart`, not `startPos`. When
		// the coarse `newSlice` was inserted with an open left boundary
		// (`openStart > 0` — the pure-add case), the recorded `startPos` was mapped
		// with a `+1`-per-open-level bias, so it sits *inside* the opened boundary.
		// A fully-closed slice's first node begins `openStart` positions earlier, so
		// walking full node sizes from `startPos` overshoots every emitted range by
		// `openStart`. Subtracting it anchors the first node at its true live start;
		// closed slices (`openStart === 0`, the update case) are unaffected.
		let cursor = coarse.startPos - coarse.newSlice.openStart;
		for (let i = 0; i < prefix; i++) {
			cursor += newNodes[i].nodeSize;
		}

		const paired = Math.min(originalMiddle.length, newMiddle.length);

		// Paired middle nodes → `update` (skip any that happen to be identical).
		for (let i = 0; i < paired; i++) {
			const newNode = newMiddle[i];
			const startPos = cursor;
			const endPos = cursor + newNode.nodeSize;
			cursor = endPos;
			if (originalMiddle[i].eq(newNode)) {
				continue;
			}
			segments.push({
				startPos,
				endPos,
				originalUserContentSlice: wholeNodeSlice(originalMiddle[i]),
				newSlice: wholeNodeSlice(newNode),
				kind: 'update',
			});
		}

		// Extra new nodes → `add`.
		for (let i = paired; i < newMiddle.length; i++) {
			const newNode = newMiddle[i];
			const startPos = cursor;
			const endPos = cursor + newNode.nodeSize;
			cursor = endPos;
			segments.push({
				startPos,
				endPos,
				originalUserContentSlice: Slice.empty,
				newSlice: wholeNodeSlice(newNode),
				kind: 'add',
			});
		}

		// Extra original nodes → `remove` (zero-width in the live doc, anchored at
		// the cursor — i.e. immediately after the paired/added content).
		for (let i = paired; i < originalMiddle.length; i++) {
			segments.push({
				startPos: cursor,
				endPos: cursor,
				originalUserContentSlice: wholeNodeSlice(originalMiddle[i]),
				newSlice: Slice.empty,
				kind: 'remove',
			});
		}
	}

	return segments.sort((a, b) => a.startPos - b.startPos);
};
